import React from 'react';
import { Box, Paper, Typography } from '@mui/material';

// Soft FORTHEYE-style card (same tokens as /home, /payslip, /attendance) — a
// touch more compact than a full content card (tighter vertical padding).
const ACCENT = '#2f6bff';
const CARD_SX = {
  px: { xs: 2, sm: 2.5, md: 3 },
  py: { xs: 1.5, sm: 1.75, md: 2 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};

/**
 * Standard employee page-header card — the "Payslip Management" strip.
 * Reused across Payslip / Leaves / Tax Calculator / Form 16 so every page opens
 * with an identically-sized icon + title + subtitle, plus an optional right-side
 * `action` slot (month picker, FY selector, buttons, status chip …).
 *
 * forwardRef + spread so it can be dropped inside an MUI <Fade>/transition.
 *
 * Props:
 *   icon      – an MUI icon element, e.g. <ReceiptLongIcon /> (rendered at its
 *               default size inside a 46×46 accent tile)
 *   title     – page title string/node
 *   subtitle  – optional caption line under the title (string or JSX node)
 *   action    – optional right-aligned node (controls/buttons/chips)
 *   accent    – tile/icon colour (defaults to the portal blue)
 *   sx        – extra sx merged onto the outer Paper (e.g. override mb)
 */
const PageHeader = React.forwardRef(function PageHeader(
  { icon, title, subtitle, action, accent = ACCENT, sx, ...rest },
  ref,
) {
  return (
    <Paper ref={ref} elevation={0} {...rest} sx={{ ...CARD_SX, mb: { xs: 1.5, md: 2.5 }, ...sx }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: { xs: 1.5, md: 2.5 } }}>
        {/* Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box sx={{
            width: 46, height: 46, borderRadius: '14px',
            bgcolor: `${accent}14`, color: accent,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.1rem', md: '1.35rem' }, color: '#111827', lineHeight: 1.2 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: '#6b7280' }}>{subtitle}</Typography>
            )}
          </Box>
        </Box>

        {/* Optional right-side controls */}
        {action && <Box sx={{ minWidth: 0 }}>{action}</Box>}
      </Box>
    </Paper>
  );
});

export default PageHeader;
